import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { JsonLd, ORGANIZATION, SITE_URL } from "@/components/JsonLd";
import type { Language } from "@/types/i18n";
import { healthConditionsStrings } from "@/locales/modules/healthConditions";
import { healthAllergiesMedsStrings } from "@/locales/modules/healthAllergiesMeds";
import { cardEmergencyStrings } from "@/locales/modules/healthMore/cardEmergency";

/**
 * /emergency-medical-card — a free, printable emergency card in up to five
 * languages, with no account (MAR-012 §4).
 *
 * The page is the link-earning asset of MAR-012: a tool people can use and
 * point to, where a product page is something nobody links to. It is also the
 * shortest way into the product — the card it prints is the paper version of
 * the one HealthPass keeps current behind a QR code.
 *
 * Nothing typed here leaves the browser: no request is made, nothing is
 * stored, and closing the tab forgets it. That is the promise the page makes,
 * so it must stay true; a test asserts that no fetch happens.
 *
 * The translations are the app's own (the condition, allergen and emergency
 * card strings every traveller's card already uses), so a term reads the same
 * on the printed card as on the QR card. The terms below are the
 * emergency-relevant ones the app's lists lack.
 */

const LANGUAGES: { code: Language; name: string }[] = [
  { code: "en", name: "English" },
  { code: "de", name: "German" },
  { code: "fr", name: "French" },
  { code: "es", name: "Spanish" },
  { code: "pt", name: "Portuguese" },
];

type Term = Record<Language, string>;

const EXTRA_CONDITIONS: Record<string, Term> = {
  insulinDiabetes: {
    en: "Insulin-dependent diabetes",
    de: "Insulinpflichtiger Diabetes",
    fr: "Diabète insulinodépendant",
    es: "Diabetes insulinodependiente",
    pt: "Diabetes insulinodependente",
  },
  epilepsy: {
    en: "Epilepsy",
    de: "Epilepsie",
    fr: "Épilepsie",
    es: "Epilepsia",
    pt: "Epilepsia",
  },
  pacemaker: {
    en: "Pacemaker",
    de: "Herzschrittmacher",
    fr: "Stimulateur cardiaque",
    es: "Marcapasos",
    pt: "Pacemaker cardíaco",
  },
  anticoagulants: {
    en: "Takes blood thinners (anticoagulants)",
    de: "Nimmt Gerinnungshemmer (Antikoagulanzien)",
    fr: "Sous anticoagulants",
    es: "Toma anticoagulantes",
    pt: "Toma anticoagulantes",
  },
  spinalCordInjury: {
    en: "Spinal cord injury",
    de: "Rückenmarksverletzung (Querschnittlähmung)",
    fr: "Lésion de la moelle épinière",
    es: "Lesión medular",
    pt: "Lesão medular",
  },
  // The emergency a first responder abroad is least likely to recognise in a
  // spinal cord injury, so the card says what it is, not only its name.
  autonomicDysreflexia: {
    en: "At risk of autonomic dysreflexia (sudden very high blood pressure)",
    de: "Risiko einer autonomen Dysreflexie (plötzlicher starker Blutdruckanstieg)",
    fr: "Risque de dysréflexie autonome (hausse brutale de la tension artérielle)",
    es: "Riesgo de disreflexia autonómica (subida brusca de la presión arterial)",
    pt: "Risco de disreflexia autonómica (subida súbita da pressão arterial)",
  },
};

const from = <K extends string>(
  table: Record<Language, Record<K, string>>,
  key: K,
): Term =>
  Object.fromEntries(
    LANGUAGES.map(({ code }) => [code, table[code][key]]),
  ) as Term;

const CONDITIONS: Term[] = [
  from(healthConditionsStrings, "condNameDiabetes"),
  EXTRA_CONDITIONS.insulinDiabetes!,
  from(healthConditionsStrings, "condNameHypertension"),
  from(healthConditionsStrings, "condNameCoronaryArteryDisease"),
  EXTRA_CONDITIONS.pacemaker!,
  EXTRA_CONDITIONS.anticoagulants!,
  from(healthConditionsStrings, "condNameAsthma"),
  from(healthConditionsStrings, "condNameCopd"),
  EXTRA_CONDITIONS.epilepsy!,
  from(healthConditionsStrings, "condNameKidneyDisease"),
  from(healthConditionsStrings, "condNameRheumatoidArthritis"),
  from(healthConditionsStrings, "condNameDepression"),
  from(healthConditionsStrings, "condNameAnxietyDisorder"),
  EXTRA_CONDITIONS.spinalCordInjury!,
  EXTRA_CONDITIONS.autonomicDysreflexia!,
];

const ALLERGENS: Term[] = (
  [
    "algAllergenPenicillin",
    "algAllergenAmoxicillin",
    "algAllergenAspirin",
    "algAllergenIbuprofen",
    "algAllergenParacetamol",
    "algAllergenAnesthetics",
    "algAllergenLatex",
    "algAllergenPeanuts",
    "algAllergenNuts",
    "algAllergenSeafood",
    "algAllergenEggs",
    "algAllergenCowsMilk",
    "algAllergenBeeStings",
    "algAllergenWaspStings",
  ] as const
).map((key) => from(healthAllergiesMedsStrings, key));

const LABEL = {
  title: from(cardEmergencyStrings, "emergxPdfTitle"),
  born: from(cardEmergencyStrings, "emergxPdfDateOfBirth"),
  blood: from(cardEmergencyStrings, "emergxPdfBloodType"),
  conditions: from(healthConditionsStrings, "condTitle"),
  allergies: from(cardEmergencyStrings, "emergxPdfAllergies"),
  medications: from(cardEmergencyStrings, "emergxPdfMedications"),
  contacts: from(cardEmergencyStrings, "emergxPdfContacts"),
  disclaimer: from(cardEmergencyStrings, "emergxPdfDisclaimer"),
};

const BLOOD_TYPES = ["A+", "A−", "B+", "B−", "AB+", "AB−", "O+", "O−"];

/** A term in every chosen language, each spelling once ("Latex", not "Latex · Latex"). */
export function inLanguages(term: Term, languages: Language[]): string {
  return [...new Set(languages.map((code) => term[code]))].join(" · ");
}

const FAQS = [
  {
    question: "Is the emergency card really free?",
    answer:
      "Yes. You fill it in on this page and print it, with no account and no payment. HealthPass makes money from its paid plans, not from this card.",
  },
  {
    question: "Is what I type stored anywhere?",
    answer:
      "No. The card is put together in your browser. Nothing you enter is sent to HealthPass or anyone else, and closing the page forgets it.",
  },
  {
    question: "Which languages can the card be printed in?",
    answer:
      "English, German, French, Spanish and Portuguese, in any combination. Conditions and allergies you tick are printed in every language you choose. Medicines and anything you type yourself are printed as you wrote them, so use generic drug names, which are recognised in most countries.",
  },
  {
    question: "What should I put on an emergency card?",
    answer:
      "Your diagnoses, every medicine with its generic name and dose, your allergies and what happens when you are exposed, your blood group if you know it, implanted devices such as a pacemaker, and one emergency contact. The CDC lists the same in its advice to travellers with a chronic illness.",
  },
  {
    question: "Do I still need my phone's medical ID?",
    answer:
      "Yes, set that up as well. A first responder looks at the lock screen first. The printed card works when the phone is locked, lost or out of battery, and it is in the language of the country you are in.",
  },
];

interface CardState {
  name: string;
  born: string;
  blood: string;
  conditions: number[];
  otherConditions: string;
  allergies: number[];
  otherAllergies: string;
  medications: string;
  contactName: string;
  contactPhone: string;
  languages: Language[];
}

const EMPTY: CardState = {
  name: "",
  born: "",
  blood: "",
  conditions: [],
  otherConditions: "",
  allergies: [],
  otherAllergies: "",
  medications: "",
  contactName: "",
  contactPhone: "",
  languages: ["en", "es"],
};

const toggle = <T,>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const lines = (text: string) =>
  text
    .split(/\n|,/)
    .map((line) => line.trim())
    .filter(Boolean);

export function EmergencyCardToolPage() {
  const [card, setCard] = useState<CardState>(EMPTY);
  const set = <K extends keyof CardState>(key: K, value: CardState[K]) =>
    setCard((current) => ({ ...current, [key]: value }));

  const langs = useMemo(
    () =>
      LANGUAGES.map((l) => l.code).filter((code) =>
        card.languages.includes(code),
      ),
    [card.languages],
  );
  const label = (term: Term) => inLanguages(term, langs);

  const conditions = [
    ...card.conditions.map((i) => label(CONDITIONS[i]!)),
    ...lines(card.otherConditions),
  ];
  const allergies = [
    ...card.allergies.map((i) => label(ALLERGENS[i]!)),
    ...lines(card.otherAllergies),
  ];
  const medications = lines(card.medications);

  const input =
    "w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none";

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Free emergency medical card for travel, in five languages"
        description="Make a free, printable emergency medical card in English, German, French, Spanish or Portuguese: conditions, allergies, medicines, blood type and an emergency contact. No sign-up; nothing you type leaves your browser."
        keywords="free emergency medical card, printable medical wallet card, medical emergency card travel, allergy card spanish, medical card in multiple languages"
        url={`${SITE_URL}/emergency-medical-card`}
        canonical={`${SITE_URL}/emergency-medical-card`}
      />
      <JsonLd
        data={{
          "@type": "WebApplication",
          name: "Free emergency medical card",
          url: `${SITE_URL}/emergency-medical-card`,
          applicationCategory: "HealthApplication",
          operatingSystem: "Any (web browser)",
          inLanguage: ["en", "de", "fr", "es", "pt"],
          description:
            "A printable emergency medical card in up to five languages, made in the browser with no account.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          publisher: { "@id": ORGANIZATION["@id"] },
        }}
      />
      <JsonLd
        data={{
          "@type": "FAQPage",
          mainEntity: FAQS.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        }}
      />
      {/* Only the card goes to the printer, whatever the app shell around it. */}
      <style>{`@media print {
  body * { visibility: hidden; }
  #emergency-card, #emergency-card * { visibility: visible; }
  #emergency-card { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none; }
}`}</style>

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white pb-14 pt-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-5xl font-bold mb-6">
            Free emergency medical card for travel, in five languages
          </h1>
          <p className="text-lg md:text-xl text-blue-100">
            Fill in your conditions, allergies, medicines, blood type and an
            emergency contact, choose the languages, and print a card a doctor
            abroad can read. English, German, French, Spanish and Portuguese, in
            any combination. No sign-up, and nothing you type leaves your
            browser.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid gap-10 lg:grid-cols-2">
        <form
          className="space-y-6"
          onSubmit={(event) => event.preventDefault()}
          aria-label="Emergency card details"
        >
          <fieldset>
            <legend className="font-semibold text-gray-900 mb-2">
              Card languages
            </legend>
            <div className="flex flex-wrap gap-3">
              {LANGUAGES.map(({ code, name }) => (
                <label key={code} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={card.languages.includes(code)}
                    onChange={() =>
                      set(
                        "languages",
                        card.languages.includes(code) &&
                          card.languages.length === 1
                          ? card.languages
                          : toggle(card.languages, code),
                      )
                    }
                  />
                  {name}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Full name
              </span>
              <input
                className={input}
                value={card.name}
                onChange={(e) => set("name", e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Date of birth
              </span>
              <input
                className={input}
                value={card.born}
                onChange={(e) => set("born", e.target.value)}
                placeholder="e.g. 14 Mar 1958"
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Blood type
              </span>
              <select
                className={input}
                value={card.blood}
                onChange={(e) => set("blood", e.target.value)}
              >
                <option value="">Not known</option>
                {BLOOD_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <fieldset>
            <legend className="font-semibold text-gray-900 mb-2">
              Conditions
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {CONDITIONS.map((term, i) => (
                <label key={term.en} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={card.conditions.includes(i)}
                    onChange={() =>
                      set("conditions", toggle(card.conditions, i))
                    }
                  />
                  {term.en}
                </label>
              ))}
            </div>
            <input
              className={`${input} mt-3`}
              value={card.otherConditions}
              onChange={(e) => set("otherConditions", e.target.value)}
              placeholder="Other conditions, separated by commas"
              aria-label="Other conditions"
              autoComplete="off"
            />
          </fieldset>

          <fieldset>
            <legend className="font-semibold text-gray-900 mb-2">
              Allergies
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {ALLERGENS.map((term, i) => (
                <label key={term.en} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={card.allergies.includes(i)}
                    onChange={() => set("allergies", toggle(card.allergies, i))}
                  />
                  {term.en}
                </label>
              ))}
            </div>
            <input
              className={`${input} mt-3`}
              value={card.otherAllergies}
              onChange={(e) => set("otherAllergies", e.target.value)}
              placeholder="Other allergies and the reaction, e.g. Sulfonamides (rash)"
              aria-label="Other allergies"
              autoComplete="off"
            />
          </fieldset>

          <label className="block">
            <span className="font-semibold text-gray-900">Medicines</span>
            <span className="block text-sm text-gray-600 mb-2">
              One per line, with the generic name and dose, e.g. Metformin 500
              mg twice daily.
            </span>
            <textarea
              className={input}
              rows={4}
              value={card.medications}
              onChange={(e) => set("medications", e.target.value)}
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Emergency contact
              </span>
              <input
                className={input}
                value={card.contactName}
                onChange={(e) => set("contactName", e.target.value)}
                placeholder="Name and relationship"
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Their phone, with country code
              </span>
              <input
                className={input}
                value={card.contactPhone}
                onChange={(e) => set("contactPhone", e.target.value)}
                placeholder="+1 555 0100"
                autoComplete="off"
              />
            </label>
          </div>
        </form>

        <div>
          <div
            id="emergency-card"
            className="bg-white rounded-xl border-2 border-red-600 shadow-sm p-5 text-sm text-gray-900"
          >
            <p className="text-red-700 font-bold tracking-wide">
              {label(LABEL.title)}
            </p>
            <p className="text-xl font-bold mt-2">{card.name || "—"}</p>
            <dl className="mt-3 space-y-3">
              {card.born && (
                <div>
                  <dt className="font-semibold">{label(LABEL.born)}</dt>
                  <dd>{card.born}</dd>
                </div>
              )}
              {card.blood && (
                <div>
                  <dt className="font-semibold">{label(LABEL.blood)}</dt>
                  <dd>{card.blood}</dd>
                </div>
              )}
              {conditions.length > 0 && (
                <div>
                  <dt className="font-semibold">{label(LABEL.conditions)}</dt>
                  {conditions.map((item) => (
                    <dd key={item}>{item}</dd>
                  ))}
                </div>
              )}
              {allergies.length > 0 && (
                <div>
                  <dt className="font-semibold text-red-700">
                    {label(LABEL.allergies)}
                  </dt>
                  {allergies.map((item) => (
                    <dd key={item}>{item}</dd>
                  ))}
                </div>
              )}
              {medications.length > 0 && (
                <div>
                  <dt className="font-semibold">{label(LABEL.medications)}</dt>
                  {medications.map((item) => (
                    <dd key={item}>{item}</dd>
                  ))}
                </div>
              )}
              {(card.contactName || card.contactPhone) && (
                <div>
                  <dt className="font-semibold">{label(LABEL.contacts)}</dt>
                  <dd>
                    {[card.contactName, card.contactPhone]
                      .filter(Boolean)
                      .join(", ")}
                  </dd>
                </div>
              )}
            </dl>
            <p className="mt-4 text-xs text-gray-600">
              {label(LABEL.disclaimer)}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              travelhealthpass.app/emergency-medical-card
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            className="mt-4 w-full bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-blue-700 transition"
          >
            Print the card
          </button>
          <p className="mt-2 text-sm text-gray-600">
            Choose &ldquo;Save as PDF&rdquo; in the print dialog to keep a copy
            on your phone. Print it, fold it, and carry it with your passport.
          </p>
          <div className="mt-6 rounded-lg bg-blue-50 border border-blue-200 p-4 text-gray-800">
            <p className="font-semibold">Want a card that stays up to date?</p>
            <p className="mt-1">
              HealthPass keeps the same information on your phone behind a QR
              code. A doctor scans it and reads it in their language, and you
              change it whenever a medicine changes. The card is free.
            </p>
            <Link
              to="/traveler/register"
              className="inline-block mt-3 text-blue-700 font-semibold hover:underline"
            >
              Create your free QR emergency card
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-12">
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Why a card in the local language
          </h2>
          <p className="text-gray-700">
            Your phone&apos;s medical ID shows what you typed, in your own
            language. A doctor in Lisbon, Lyon or Cancún may not read it. The
            conditions and allergies on this card are printed in every language
            you choose, using the same medical terms HealthPass uses on its QR
            cards. Pick your own language and the languages of the countries on
            your route.
          </p>
          <p className="text-gray-700 mt-3">
            Travelling with medicines? Prepare a{" "}
            <Link
              to="/medication-travel-letter"
              className="text-blue-700 hover:underline"
            >
              doctor&apos;s letter for your medication
            </Link>{" "}
            in the same languages. For what else to prepare, see{" "}
            <a
              href="/blog/apps-to-carry-medical-information-when-traveling"
              className="text-blue-700 hover:underline"
            >
              the apps that carry your medical information abroad, compared
            </a>{" "}
            and the{" "}
            <a
              href="https://www.cdc.gov/yellow-book/hcp/travelers-with-additional-considerations/travelers-with-chronic-illnesses.html"
              className="text-blue-700 hover:underline"
            >
              CDC&apos;s advice for travellers with a chronic illness
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {FAQS.map((faq) => (
              <div key={faq.question}>
                <h3 className="text-lg font-semibold text-gray-900">
                  {faq.question}
                </h3>
                <p className="text-gray-700 mt-1">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
